import { useEffect, useState } from "react";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { ApplicationSupplierDetail } from "../../models/supplier/application-supplier-detail";
import { ApplicationDay } from "../../models/supplier/application-day";
import { fetchSupplier, insertSupplier, updateSupplier } from "../../store/features/supplier/supplier-slice";
import { fetchDepartments } from "../../store/features/organisation/department-slice";
import { arrayRequired, emailValidator, phoneValidator, postcodeValidator, requiredValidator } from "../../helpers/validators";
import { SelectChangeEvent } from "@mui/material/Select/Select";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { showSuccess } from "../../components/notification/toastr-actions";
import { FeatureCard, FeatureCardColumn, FeatureCardRow, FeatureCardSingleColumn, FeatureCardTitle, FeatureCardRowApproval } from "../../styles/shared/card";
import TextInput from "../../components/controls/text-input";
import Dropdown from "../../components/controls/dropdown";
import TimePicker from "../../components/controls/time-picker";
import dayjs from "dayjs";
import Checkbox from "../../components/controls/checkbox";
import Button from "../../components/controls/button";
import styled from "styled-components";
import { closeDrawer } from "../../store/features/drawer/drawer-slice";
import { closeConfirm, openConfirm } from "../../store/features/notification/confirm-slice";

interface SupplierModalProps {
    id: number;
}

interface Validation {
    name: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressLine3: string | null;
    city: string | null;
    postcode: string | null;
    phone: string | null;
    email: string | null;
    departments: string | null;
    orderCutOffTime: string | null;
    branches: string | null;
    minimumPallets: string | null;
}


export default function SupplierDrawer({ id }: SupplierModalProps) {
    const dispatch = useAppDispatch();
    const departments = useAppSelector((state) => state.department).departments?.departments ?? [];
    const branches = useAppSelector((state) => state.user).user?.branches;
    const [newBranchId, setNewBranchId] = useState<string | null>(null);
    const [newBranchDeliveryDays, setNewBranchDeliveryDays] = useState<string[]>([]);
    const [newBranchShipToNumber, setNewBranchShipToNumber] = useState("");
    const [newBranchBillToNumber, setNewBranchBillToNumber] = useState("");
    const [newBranchAccountNumber, setNewBranchAccountNumber] = useState("");
    const [originalOrderingType, setOriginalOrderingType] = useState<ApplicationSupplierDetail["productOrderingType"]>("Cases");


    const [validation, setValidation] = useState<Validation>({
        name: null,
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        city: null,
        postcode: null,
        phone: null,
        email: null,
        departments: null,
        orderCutOffTime: null,
        branches: null,
        minimumPallets: null,
    });

    const [supplier, setSupplier] = useState<ApplicationSupplierDetail>({
        id: 0,
        name: "",
        noOfProducts: 0,
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        city: "",
        postcode: "",
        phone: "",
        email: "",
        updated: new Date(),
        apiIntegration: false,
        emailIntegration: false,
        whatsappIntegration: false,
        departmentIds: [],
        orderCutOffTime: "",
        branches: [],
        requiresOrderApproval: false,
        minimumQuantity: 0,
        minimumValue: 0,
        productOrderingType: "Cases",
        minimumPallets: null,
    });

    const [departmentOptions, setDepartmentOptions] = useState<
        { value: string; label: string }[]
    >([{ value: "", label: "" }]);

    const [branchOptions, setBranchOptions] = useState<
        { value: string; label: string }[]
    >([{ value: "", label: "" }]);

    const [deliveryDayOptions, _] = useState<{ value: string; label: string }[]>([
        { value: ApplicationDay.Monday, label: ApplicationDay.Monday },
        { value: ApplicationDay.Tuesday, label: ApplicationDay.Tuesday },
        { value: ApplicationDay.Wednesday, label: ApplicationDay.Wednesday },
        { value: ApplicationDay.Thursday, label: ApplicationDay.Thursday },
        { value: ApplicationDay.Friday, label: ApplicationDay.Friday },
        { value: ApplicationDay.Saturday, label: ApplicationDay.Saturday },
        { value: ApplicationDay.Sunday, label: ApplicationDay.Sunday },
    ]);

    useEffect(() => {
        if (id) {
            dispatch(fetchSupplier(id)).then((response) => {
                const supplier = response.payload as ApplicationSupplierDetail;
                setSupplier({
                    ...supplier,
                    email: supplier.email.split(";")
                        .map(email => email.trim())
                        .filter(email => email)
                        .join("\n")
                });
                setOriginalOrderingType(supplier.productOrderingType ?? "Cases");
            });
        }
    }, [id]);


    useEffect(() => {
        if (departments && departments.length > 0) {
            setDepartmentOptions(
                departments.map((department) => ({
                    value: department.id.toString(),
                    label: department.name,
                }))
            );
        } else {
            dispatch(fetchDepartments());
        }
    }, [departments]);

    useEffect(() => {
        if (branches) {
            setBranchOptions(
                branches.map((branch) => ({
                    value: branch.id.toString(),
                    label: branch.name,
                }))
            );
        }
    }, [branches]);

    const onChange = <K extends keyof ApplicationSupplierDetail>(
        value: ApplicationSupplierDetail[K],
        property: K,
        validation?: (input: ApplicationSupplierDetail[K]) => string | null
    ) => {
        const errorMessage = validation ? validation(value) : null;

        setSupplier((prevState) => ({
            ...prevState,
            [property]: value,
        }));

        setValidation((prevState) => ({
            ...prevState,
            [property]: errorMessage,
        }));
    };

    const handleDepartmentSelection = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event;

        const departments =
            typeof value === "string" ? value.split(",") : (value as string[]);
        const departmentIds = departments.map((x) => parseInt(x));
        setSupplier((prevState) => ({
            ...prevState,
            departmentIds: departmentIds,
        }));

        setValidation((prevState) => ({
            ...prevState,
            departments: arrayRequired(departmentIds),
        }));
    };

    const saveSupplier = async () => {
        if (!validateAndCheckErrors()) {
            return;
        }

        let notificationMessage = `${supplier.name} `;
        if (supplier.id == 0) {
            await dispatch(insertSupplier(supplier));

            notificationMessage += "created";
        } else {
            await dispatch(updateSupplier(supplier));
            notificationMessage += "updated";
        }
        dispatch(showSuccess(notificationMessage));
        dispatch(closeDrawer());
    };

    const onUpdate = async () => {
        if (supplier.id > 0 && originalOrderingType === "Pallets" && supplier.productOrderingType === "Cases") {
            dispatch(openConfirm({
                title: "Change supplier ordering type?",
                message: "Changing this supplier to Cases means its pallet-specific settings will no longer be used for new orders. Existing and historic orders will not be changed.",
                show: true,
                data: {
                    onConfirm: () => {
                        dispatch(closeConfirm());
                        void saveSupplier();
                    },
                    onCancel: () => dispatch(closeConfirm()),
                },
            }));
            return;
        }
        await saveSupplier();
    };

    const validateAndCheckErrors = () => {
        const validation = {
            name: requiredValidator(supplier.name),
            addressLine1: requiredValidator(supplier.addressLine1),
            addressLine2: null,
            addressLine3: null,
            city: requiredValidator(supplier.city),
            postcode: postcodeValidator(supplier.postcode),
            phone: phoneValidator(supplier.phone),
            email: (() => {
                const emails = supplier.email.split(/\r?\n/).map(e => e.trim());
                const invalidEmails = emails.filter((e) => e && emailValidator(e));
                return invalidEmails.length > 0 ? "Invalid email(s) found" : null;
            })(),
            departments: arrayRequired(supplier.departmentIds),
            orderCutOffTime: requiredValidator(supplier.orderCutOffTime),
            branches: arrayRequired(supplier.branches),
            minimumPallets: supplier.productOrderingType === "Pallets" && supplier.minimumPallets != null
                ? (!Number.isInteger(supplier.minimumPallets) || supplier.minimumPallets <= 0
                    ? "Enter a positive whole number of minimum pallets"
                    : null)
                : null,
        };

        setValidation(validation);

        const hasErrors = Object.values(validation).some((error) => error !== null);

        if (hasErrors) {
            return false; // Or handle the errors as needed
        } else {
            return true; // Proceed with form submission or further processing
        }
    };

    const getDepartmentSelectionName = (selected: string[]) => {
        if (selected.length > 1) {
            return "Multiple selected";
        }

        var selectedDepartments = departments
            .filter((x) => selected.indexOf(x.id.toString()) != -1)
            .map((department) => department.name)
            .join(", ");
        return selectedDepartments;
    };

    const getBranchSelectionName = (selected: string[]) => {
        if (selected.length > 1) {
            return "Multiple selected";
        }

        var selectedBranches = branches
            ?.filter((x) => selected.indexOf(x.id.toString()) != -1)
            .map((branch) => branch.name)
            .join(", ");
        return selectedBranches;
    };

    const getDeliveryDaysSelection = (selected: string[]) => {
        if (selected.length > 2) {
            return "Multiple selected";
        }

        var selectedOptions = deliveryDayOptions
            .filter((x) => selected.indexOf(x.value.toString()) != -1)
            .map((option) => option.value)
            .join(", ");

        return selectedOptions;
    };

    return (
        <Container>
            <FeatureCard>
                <FeatureCardTitle>Basic information</FeatureCardTitle>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtSupplierName"
                            value={supplier.name}
                            label="Name"
                            onChange={(event) =>
                                onChange(event.target.value, "name", (input) =>
                                    requiredValidator(input)
                                )
                            }
                            error={validation.name}
                        />
                    </FeatureCardColumn>
                    <FeatureCardColumn>
                        <Dropdown
                            id="cboDepartment"
                            variant="outlined"
                            label="Select departments"
                            renderValue={(selected) =>
                                getDepartmentSelectionName(selected as string[])
                            }
                            options={departmentOptions}
                            onChange={handleDepartmentSelection}
                            value={supplier.departmentIds?.map((x) => x.toString()) ?? []}
                            multiple
                            errorMessage={validation.departments}
                            autoWidth={false}
                            native={false}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TimePicker
                            id="orderCutOffTime"
                            label="Order cut off time"
                            value={dayjs(supplier.orderCutOffTime, "HH:mm")}
                            onChange={(value) => {
                                if (value && dayjs(value).isValid()) {
                                    onChange(
                                        value.utc(true).format("HH:mm:ss"),
                                        "orderCutOffTime",
                                        (input) => requiredValidator(input)
                                    );
                                } else {
                                    onChange("", "orderCutOffTime", (input) =>
                                        requiredValidator(input)
                                    );
                                }
                            }}
                            error={validation.orderCutOffTime}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <Button
                            id="btnAddBranch"
                            label="Add Branch"
                            variant="outlined"
                            onClick={() => setNewBranchId("")}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
                {newBranchId !== null && (
                    <FeatureCardRow>
                        <FeatureCardColumn>
                            <Dropdown
                                id="branchSelect"
                                label="Select Branch"
                                options={branchOptions.filter(opt => !supplier.branches.some(b => b.id.toString() === opt.value)
                                )}
                                value={newBranchId}
                                onChange={(e: SelectChangeEvent<unknown>) => setNewBranchId(e.target.value as string)}
                                errorMessage={!newBranchId ? "Please select a branch" : undefined} variant={"outlined"} autoWidth={false} multiple={false} native={false} />
                        </FeatureCardColumn>
                        <FeatureCardColumn>
                            <Dropdown
                                id="branchDeliveryDays"
                                label="Delivery Days"
                                multiple
                                options={deliveryDayOptions}
                                value={newBranchDeliveryDays}
                                onChange={(e) => setNewBranchDeliveryDays(typeof e.target.value === "string" ? e.target.value.split(",") as string[] : e.target.value as string[])}
                                errorMessage={newBranchDeliveryDays.length === 0 ? "Select delivery days" : undefined} variant={"outlined"} autoWidth={false} native={false} />
                        </FeatureCardColumn>
                        <FeatureCardSingleColumn>
                            <OrderDetails>
                                <strong>Order details</strong>
                                <TextInput
                                    id="branchAccountNumber"
                                    label="Account Number"
                                    value={newBranchAccountNumber}
                                    onChange={(event) => setNewBranchAccountNumber(event.target.value)}
                                />
                                <TextInput
                                    id="branchBillToNumber"
                                    label="Bill To Number"
                                    value={newBranchBillToNumber}
                                    onChange={(event) => setNewBranchBillToNumber(event.target.value)}
                                />
                                <TextInput
                                    id="branchShipToNumber"
                                    label="Ship To Number"
                                    value={newBranchShipToNumber}
                                    onChange={(event) => setNewBranchShipToNumber(event.target.value)}
                                />
                            </OrderDetails>
                        </FeatureCardSingleColumn>
                        <FeatureCardRow>
                            <FeatureCardSingleColumn>
                                <Button
                                    id="btnConfirmAddBranch"
                                    label="Add"
                                    disabled={!newBranchId || newBranchDeliveryDays.length === 0}
                                    onClick={() => {
                                        if (newBranchId && newBranchDeliveryDays.length > 0) {
                                            const selectedBranch = branches?.find(b => b.id.toString() === newBranchId);
                                            if (selectedBranch) {
                                                setSupplier(prev => ({
                                                    ...prev,
                                                    branches: [
                                                        ...prev.branches,
                                                        {
                                                            id: selectedBranch.id,
                                                            name: selectedBranch.name,
                                                            days: newBranchDeliveryDays.map(d => mapStringToEnum(ApplicationDay, d)),
                                                            accountNumber: newBranchAccountNumber.trim(),
                                                            billToNumber: newBranchBillToNumber.trim(),
                                                            shipToNumber: newBranchShipToNumber.trim(),
                                                        }
                                                    ]
                                                }));
                                                // Reset add form
                                                setNewBranchId(null);
                                                setNewBranchDeliveryDays([]);
                                                setNewBranchAccountNumber("");
                                                setNewBranchBillToNumber("");
                                                setNewBranchShipToNumber("");
                                            }
                                        }
                                    }} variant={"outlined"} />
                            </FeatureCardSingleColumn>
                        </FeatureCardRow>
                    </FeatureCardRow>
                )}
                {supplier.branches.length > 0 && (
                    <>
                        {validation.branches && <BranchValidation>{validation.branches}</BranchValidation>}
                        <FeatureCardRow>
                        {supplier.branches.map((branch) => (
                            <FeatureCardColumn key={branch.id}>
                                <div>
                                    <strong>{branch.name}</strong> - {branch.days?.join(", ")}
                                    {(branch.accountNumber || branch.billToNumber || branch.shipToNumber) && (
                                        <OrderDetails>
                                            <strong>Order details</strong>
                                            {branch.accountNumber && <span>Account Number: {branch.accountNumber}</span>}
                                            {branch.billToNumber && <span>Bill To Number: {branch.billToNumber}</span>}
                                            {branch.shipToNumber && <span>Ship To Number: {branch.shipToNumber}</span>}
                                        </OrderDetails>
                                    )}
                                    <Button
                                        variant="text"
                                        label="Remove"
                                        onClick={() => {
                                            setSupplier(prev => ({
                                                ...prev,
                                                branches: prev.branches.filter(b => b.id !== branch.id)
                                            }));
                                        }} id={""} />
                                </div>
                            </FeatureCardColumn>
                        ))}
                        </FeatureCardRow>
                    </>
                )}
            </FeatureCard>
            <FeatureCard>
                <FeatureCardTitle>Location</FeatureCardTitle>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtAddressLine1"
                            value={supplier.addressLine1}
                            label="Address line 1"
                            onChange={(event) =>
                                onChange(event.target.value, "addressLine1", (input) =>
                                    requiredValidator(input)
                                )
                            }
                            error={validation.addressLine1}
                        />
                    </FeatureCardColumn>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtAddressLine2"
                            value={supplier.addressLine2}
                            label="Address line 2"
                            onChange={(event) => onChange(event.target.value, "addressLine2")}
                            error={validation.addressLine2}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtAddressLine3"
                            value={supplier.addressLine3}
                            label="Address line 3"
                            onChange={(event) => onChange(event.target.value, "addressLine3")}
                            error={validation.addressLine3}
                        />
                    </FeatureCardColumn>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtCity"
                            value={supplier.city}
                            label="City"
                            onChange={(event) =>
                                onChange(event.target.value, "city", (input) =>
                                    requiredValidator(input)
                                )
                            }
                            error={validation.city}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtPostcode"
                            value={supplier.postcode}
                            label="Postcode"
                            onChange={(event) =>
                                onChange(event.target.value, "postcode", (input) =>
                                    postcodeValidator(input)
                                )
                            }
                            error={validation.postcode}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
            </FeatureCard>

            <FeatureCard>
                <FeatureCardTitle>Contact information</FeatureCardTitle>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtPhone"
                            value={supplier.phone}
                            label="Phone"
                            onChange={(event) =>
                                onChange(event.target.value, "phone", (input) =>
                                    phoneValidator(input)
                                )
                            }
                            error={validation.phone}
                        />
                    </FeatureCardColumn>
                    <FeatureCardColumn>
                        <TextInput
                            id="txtEmail"
                            value={supplier.email}
                            label="Email addresses (One email per line)"
                            onChange={(event) =>
                                onChange(event.target.value, "email", (input) => {
                                    const emails = input.split(/\r?\n/).map(e => e.trim());
                                    const invalidEmails = emails.filter(e => e && emailValidator(e));
                                    return invalidEmails.length > 0 ? "Invalid email(s) found" : null;
                                })
                            }
                            error={validation.email}
                            multiline
                            rows={3}
                        />
                    </FeatureCardColumn>
                </FeatureCardRow>
            </FeatureCard>

            <FeatureCard>
                <FeatureCardTitle>Automation</FeatureCardTitle>
                <FeatureCardRow>
                    <FeatureCardColumn>
                        <Checkbox
                            label="Email"
                            checked={supplier.emailIntegration}
                            onChange={(event) =>
                                onChange(event.target.checked, "emailIntegration")
                            }
                        ></Checkbox>
                    </FeatureCardColumn>
                    <FeatureCardColumn>
                        <Checkbox
                            label="Whatsapp"
                            checked={supplier.whatsappIntegration}
                            onChange={(event) =>
                                onChange(event.target.checked, "whatsappIntegration")
                            }
                        ></Checkbox>
                    </FeatureCardColumn>
                </FeatureCardRow>
            </FeatureCard>

            <FeatureCard>
                <FeatureCardTitle>Approvals</FeatureCardTitle>
                
                <FeatureCardRow>
                {supplier.productOrderingType === "Cases" && (
                    <FeatureCardColumn>
                        <TextInput
                            type="number"
                            id="txtMinimumQuantity"
                            label="Minimum Quantity"
                            value={supplier.minimumQuantity.toString() ?? ""}
                            onChange={(event) =>
                                onChange(event.target.value ? parseFloat(event.target.value) : 0, "minimumQuantity")} />
                    </FeatureCardColumn>
      )}
                    <FeatureCardColumn>
                        <TextInput
                            type="number"
                            id="txtMinimumValue"
                            label="Minimum Value"
                            value={supplier.minimumValue.toString() ?? ""}
                            onChange={(event) =>
                                onChange(event.target.value ? parseFloat(event.target.value) : 0, "minimumValue")} />
                    </FeatureCardColumn>
                </FeatureCardRow>
          
                 <FeatureCardRow>
                    <FeatureCardColumn>
                        <Dropdown
                            id="cboProductOrderingType"
                            label="Product Ordering Type"
                            options={[{ value: "Cases", label: "Cases" }, { value: "Pallets", label: "Pallets" }]}
                            value={supplier.productOrderingType}
                            onChange={(event) => onChange(event.target.value as ApplicationSupplierDetail["productOrderingType"], "productOrderingType")}
                            errorMessage={null}
                            variant="outlined"
                            autoWidth={false}
                            multiple={false}
                            native={false}
                        />
                    </FeatureCardColumn>
                    {supplier.productOrderingType === "Pallets" && (
                        <FeatureCardColumn>
                            <TextInput
                                type="number"
                                id="txtMinimumPallets"
                                label="Minimum Pallets"
                                value={supplier.minimumPallets?.toString() ?? ""}
                                error={validation.minimumPallets}
                                onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value), "minimumPallets")}
                            />
                            <FieldHint>Optional. The minimum combined pallet equivalent required across all products ordered from this supplier.</FieldHint>
                        </FeatureCardColumn>
                    )}
                </FeatureCardRow>
                <FeatureCardRowApproval>
                    <FeatureCardSingleColumn>
                        <Checkbox
                            label="Requires Order Approval"
                            checked={supplier.requiresOrderApproval}
                            onChange={(event) =>
                                onChange(event.target.checked, "requiresOrderApproval")
                            }
                        ></Checkbox>
                    </FeatureCardSingleColumn>
                </FeatureCardRowApproval>
            </FeatureCard>

            <ButtonContainer>
                <Button
                    id="btnCreate"
                    label={supplier.id == 0 ? "Create" : "Update"}
                    variant="contained"
                    onClick={onUpdate}
                ></Button>
            </ButtonContainer>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding:10px;
  `;

const FieldHint = styled.small`
    display: block;
    margin-top: 4px;
    color: #555;
`;

const BranchValidation = styled.div`
    color: #d32f2f;
    margin: 8px 0;
`;

const OrderDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 8px 0;
`;

const Title = styled.div`
    text-align: center;
    font-size: 1.5rem;
    margin-bottom: 30px;
    padding-bottom: 10px;
    border-bottom: 3px solid ${(props) => props.theme.application.scheme.primary};
  `;

const ButtonContainer = styled.div`
  display:flex;
  margin-top:10px;
      justify-content: flex-end;
  
      button:first-of-type{
          margin-right:10px;
      }
  }
`